import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faStore,
    faArrowLeft,
    faInfoCircle,
    faCheckCircle,
    faUsers,
    faGift,
    faChartLine,
    faQrcode,
    faBullhorn
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function UsuariosFranquiciado() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center mb-4">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mr-4">
                            <FontAwesomeIcon icon={faStore} className="text-green-600 text-3xl" />
                        </div>
                        <div>
                            <h1 className="text-4xl font-bold text-gray-800 mb-2">
                                Franquiciado
                            </h1>
                            <p className="text-xl text-gray-600">
                                Dueño de franquicia con acceso a estadísticas y gestión operativa de sus tiendas.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Introducción */}
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-lg mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3 flex items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-green-600" />
                        ¿Qué es un Franquiciado?
                    </h2>
                    <p className="text-gray-700 mb-4">
                        Un <strong>Franquiciado</strong> es el dueño de una franquicia que tiene acceso a PetitPrime para gestionar sus tiendas asignadas. Los franquiciados tienen acceso similar a los trabajadores (gestionar clientes, acumular puntos, canjear premios) pero con la capacidad adicional de ver estadísticas y métricas de rendimiento de sus tiendas.
                    </p>
                    <p className="text-gray-700">
                        Este rol es ideal para dueños de franquicias que necesitan ver el rendimiento de sus tiendas además de gestionar las operaciones diarias, pero no requieren acceso a configuraciones globales de marca o funciones de marketing avanzadas.
                    </p>
                </div>

                {/* Acceso */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        ¿Cómo acceder?
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Los franquiciados acceden a PetitPrime de manera similar a los trabajadores:
                        </p>
                        <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                            <li>El dueño de la marca debe agregarte como franquiciado a una tienda desde la gestión de tiendas</li>
                            <li>Recibirás un email con las instrucciones para registrarte</li>
                            <li>Haz clic en el enlace del email o ve a la página de login de franquiciados</li>
                            <li>Completa el formulario de registro con tus datos</li>
                            <li>Inicia sesión con tu email y contraseña</li>
                            <li>Serás redirigido a tu dashboard de franquiciado</li>
                        </ol>
                        <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                            <p className="text-sm text-gray-700">
                                <strong>Nota:</strong> Solo puedes acceder a las tiendas que el dueño de la marca te haya asignado. No podrás ver ni gestionar otras tiendas.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Dashboard de Franquiciado */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        1. Dashboard de Franquiciado
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Al iniciar sesión, verás tu dashboard personalizado con información sobre tus tiendas asignadas:
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faStore} className="mr-2 text-green-600" />
                                    Tiendas Asociadas
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Número total de tiendas que tienes asignadas y a las que puedes acceder.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faBullhorn} className="mr-2 text-blue-600" />
                                    Campañas Activas
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Número de campañas de juego o promociones activas en tus tiendas.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faChartLine} className="mr-2 text-purple-600" />
                                    Acceso a Estadísticas
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Puedes ver estadísticas y métricas de rendimiento de tus tiendas.
                                </p>
                            </div>
                        </div>

                        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                            <p className="text-sm text-gray-700">
                                <strong>💡 Tip:</strong> Desde el dashboard puedes acceder a todas tus tiendas asignadas y ver tanto las operaciones diarias como las estadísticas de rendimiento.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Funcionalidades Disponibles */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        2. Funcionalidades Disponibles
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Como franquiciado, tienes acceso a todas las funcionalidades de trabajador <strong>más</strong> acceso a estadísticas:
                        </p>

                        <div className="space-y-4">
                            {/* Funcionalidades de Trabajador */}
                            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                    Funcionalidades de Trabajador (Todas Disponibles)
                                </h3>
                                <ul className="list-disc list-inside text-sm text-gray-700 space-y-2 ml-4">
                                    <li><strong>Gestionar Clientes:</strong> Ver, buscar, crear y gestionar clientes de tus tiendas</li>
                                    <li><strong>Acumular Puntos:</strong> Acumular puntos a clientes cuando realizan compras</li>
                                    <li><strong>Canjear Premios:</strong> Ayudar a clientes a canjear premios y aplicar descuentos</li>
                                    <li><strong>Enviar Códigos de Campañas:</strong> Enviar códigos de recompensa de campañas activas</li>
                                </ul>
                            </div>

                            {/* Estadísticas - Diferencia Clave */}
                            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                    <FontAwesomeIcon icon={faChartLine} className="mr-2 text-green-600" />
                                    Estadísticas y Métricas (Exclusivo de Franquiciados)
                                </h3>
                                <p className="text-sm text-gray-700 mb-2">
                                    Como franquiciado, puedes ver estadísticas detalladas de tus tiendas asignadas:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-700 ml-4 space-y-2">
                                    <li><strong>Estadísticas de Tienda:</strong> Ver métricas completas de cada tienda asignada</li>
                                    <li><strong>Total de Clientes:</strong> Número de clientes únicos que han visitado la tienda</li>
                                    <li><strong>Ventas Totales:</strong> Suma total de ventas realizadas en la tienda</li>
                                    <li><strong>Tasa de Conversión:</strong> Porcentaje de clientes que realizan compras</li>
                                    <li><strong>Valor Promedio de Transacción:</strong> Promedio del valor de cada compra</li>
                                    <li><strong>Tasa de Redención de Puntos:</strong> Porcentaje de puntos canjeados</li>
                                    <li><strong>Frecuencia de Visitas:</strong> Patrones de visita de los clientes</li>
                                    <li><strong>Productividad:</strong> Métricas de rendimiento operativo</li>
                                </ul>
                                <div className="mt-3 bg-white p-3 rounded border border-green-200">
                                    <p className="text-xs text-gray-600">
                                        <strong>💡 Esta es la diferencia clave:</strong> Los trabajadores NO tienen acceso a estas estadísticas, pero los franquiciados sí. Esto te permite tomar decisiones informadas sobre el rendimiento de tus tiendas.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Diferencias con Trabajador */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        3. Diferencias con Trabajador
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            La principal diferencia entre un Franquiciado y un Trabajador es el acceso a estadísticas:
                        </p>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Funcionalidad</th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Trabajador</th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Franquiciado</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Gestionar Clientes</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                    </tr>
                                    <tr className="bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Acumular Puntos</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                    </tr>
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Canjear Premios</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                    </tr>
                                    <tr className="bg-green-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">Ver Estadísticas</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><span className="text-gray-400">—</span></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                    </tr>
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Ver Reportes</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><span className="text-gray-400">—</span></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Limitaciones */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        4. Limitaciones
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Como franquiciado, <strong>NO</strong> tienes acceso a:
                        </p>
                        <ul className="space-y-3">
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">✗</span>
                                <div>
                                    <strong className="text-gray-800">Configuración de marcas y tiendas:</strong> No puedes crear, editar o eliminar marcas o tiendas.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">✗</span>
                                <div>
                                    <strong className="text-gray-800">Marketing y campañas:</strong> No puedes crear ofertas, configurar WinBack, o gestionar campañas de marketing avanzadas.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">✗</span>
                                <div>
                                    <strong className="text-gray-800">Gestión de productos:</strong> No puedes agregar, editar o eliminar productos del catálogo.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">✗</span>
                                <div>
                                    <strong className="text-gray-800">Otras tiendas:</strong> Solo puedes acceder a las tiendas que te hayan sido asignadas específicamente.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">✗</span>
                                <div>
                                    <strong className="text-gray-800">Estadísticas globales:</strong> Solo puedes ver estadísticas de tus tiendas asignadas, no de toda la marca.
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Mejores Prácticas */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        5. Mejores Prácticas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Revisa las estadísticas regularmente:</strong> Usa las métricas disponibles para identificar tendencias y oportunidades de mejora en tus tiendas.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Compara el rendimiento:</strong> Si tienes múltiples tiendas asignadas, compara sus métricas para identificar mejores prácticas.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Toma decisiones basadas en datos:</strong> Usa las estadísticas para tomar decisiones informadas sobre horarios, personal, y estrategias de venta.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Gestiona operaciones diarias:</strong> Aprovecha las funcionalidades de trabajador para gestionar clientes y puntos eficientemente.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Comunica con el dueño:</strong> Si identificas oportunidades o problemas en las estadísticas, comunícalos al dueño de la marca para acciones coordinadas.
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Troubleshooting */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        6. Solución de Problemas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">No puedo iniciar sesión</h4>
                                <p className="text-gray-700 text-sm">
                                    Verifica que el dueño de la marca te haya agregado como franquiciado a una tienda. Si acabas de ser agregado, espera unos minutos y vuelve a intentar. Si el problema persiste, contacta con el dueño de la marca.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">No veo estadísticas de mi tienda</h4>
                                <p className="text-gray-700 text-sm">
                                    Asegúrate de que la tienda tenga transacciones registradas. Las estadísticas se calculan desde los tickets de compra. Si acabas de empezar, es normal que las métricas estén en cero hasta que haya ventas.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Las estadísticas parecen incorrectas</h4>
                                <p className="text-gray-700 text-sm">
                                    Verifica que estés viendo las estadísticas de la tienda correcta. Las estadísticas se calculan en tiempo real desde los tickets registrados en el sistema.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Quiero acceso a más funcionalidades</h4>
                                <p className="text-gray-700 text-sm">
                                    Si necesitas acceso a configuraciones de marca, marketing avanzado, o gestión de productos, habla con el dueño de la marca. Estas funcionalidades están reservadas para el dueño de la marca.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-8 border-t">
                    <Link
                        to="/documentation/usuarios/trabajador"
                        className="inline-flex items-center text-green-600 hover:text-green-800 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Anterior: Trabajador
                    </Link>
                    <Link
                        to="/documentation/usuarios"
                        className="inline-flex items-center text-green-600 hover:text-green-800 font-medium"
                    >
                        Volver a Tipos de Usuarios
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default UsuariosFranquiciado;

